import { useEffect } from 'react';
import { useToastStore } from '../store/useToastStore';

const SELECTED_CELL_CLASS = 'marketing-table-copy-cell-selected';
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [contenteditable="true"], [role="button"], [data-disable-cell-copy]';

type TableSelection = {
  table: HTMLTableElement;
  anchor: HTMLTableCellElement;
  focus: HTMLTableCellElement;
};

type CellPosition = {
  row: number;
  column: number;
  rowSpan: number;
  colSpan: number;
};

type TableGrid = {
  rows: HTMLTableRowElement[];
  cells: Map<HTMLTableCellElement, CellPosition>;
  slots: Array<Array<HTMLTableCellElement | undefined>>;
};

type CellBounds = {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
};

const isEditableElement = (element: Element | null) =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLTextAreaElement ||
  element instanceof HTMLSelectElement ||
  element?.getAttribute('contenteditable') === 'true';

const getTableCell = (target: EventTarget | null) => {
  if (!(target instanceof Element) || target.closest(INTERACTIVE_SELECTOR)) return null;
  const cell = target.closest('td, th');
  if (!(cell instanceof HTMLTableCellElement)) return null;
  const table = cell.closest('table');
  if (!(table instanceof HTMLTableElement) || !table.closest('.content-inner')) return null;
  return cell;
};

const tableGrid = (table: HTMLTableElement): TableGrid => {
  const rows = Array.from(table.rows);
  const cells = new Map<HTMLTableCellElement, CellPosition>();
  const slots: Array<Array<HTMLTableCellElement | undefined>> = [];

  rows.forEach((row, rowIndex) => {
    slots[rowIndex] ??= [];
    let column = 0;

    Array.from(row.cells).forEach(cell => {
      while (slots[rowIndex][column]) column += 1;

      const rowSpan = Math.max(cell.rowSpan, 1);
      const colSpan = Math.max(cell.colSpan, 1);
      cells.set(cell, { row: rowIndex, column, rowSpan, colSpan });

      for (let spannedRow = rowIndex; spannedRow < rowIndex + rowSpan; spannedRow += 1) {
        slots[spannedRow] ??= [];
        for (let spannedColumn = column; spannedColumn < column + colSpan; spannedColumn += 1) {
          slots[spannedRow][spannedColumn] = cell;
        }
      }
      column += colSpan;
    });
  });

  return { rows, cells, slots };
};

const selectionBounds = (selection: TableSelection, grid = tableGrid(selection.table)): CellBounds | null => {
  const anchor = grid.cells.get(selection.anchor);
  const focus = grid.cells.get(selection.focus);
  if (!anchor || !focus) return null;

  return {
    startRow: Math.min(anchor.row, focus.row),
    endRow: Math.max(anchor.row + anchor.rowSpan - 1, focus.row + focus.rowSpan - 1),
    startColumn: Math.min(anchor.column, focus.column),
    endColumn: Math.max(anchor.column + anchor.colSpan - 1, focus.column + focus.colSpan - 1),
  };
};

const cellRange = (selection: TableSelection) => {
  const grid = tableGrid(selection.table);
  const bounds = selectionBounds(selection, grid);
  if (!bounds) return [];

  return Array.from(grid.cells.entries())
    .filter(([, position]) =>
      position.row <= bounds.endRow &&
      position.row + position.rowSpan - 1 >= bounds.startRow &&
      position.column <= bounds.endColumn &&
      position.column + position.colSpan - 1 >= bounds.startColumn,
    )
    .map(([cell]) => cell);
};

const cellText = (cell: HTMLTableCellElement) =>
  (cell.innerText || cell.textContent || '').replace(/\s+/g, ' ').trim();

const selectionText = (selection: TableSelection) => {
  const grid = tableGrid(selection.table);
  const bounds = selectionBounds(selection, grid);
  if (!bounds) return '';

  return grid.rows
    .slice(bounds.startRow, bounds.endRow + 1)
    .map((_, rowOffset) => {
      const row = bounds.startRow + rowOffset;
      return Array.from({ length: bounds.endColumn - bounds.startColumn + 1 }, (_, columnOffset) => {
        const column = bounds.startColumn + columnOffset;
        const cell = grid.slots[row]?.[column];
        const position = cell ? grid.cells.get(cell) : undefined;
        // Keep the table's logical shape: a merged cell has text only at its
        // origin, while its covered positions remain empty TSV fields.
        return cell && position?.row === row && position.column === column ? cellText(cell) : '';
      }).join('\t');
    })
    .join('\n');
};

/**
 * Enables spreadsheet-like rectangular selection and TSV copying for all
 * tables shown under 营销分析. It deliberately ignores controls inside cells
 * so tree expansion, links, and form elements keep their existing behavior.
 */
export function MarketingTableCopySupport({ enabled }: { enabled: boolean }) {
  const showToast = useToastStore(state => state.show);

  useEffect(() => {
    if (!enabled) return;

    document.body.classList.add('marketing-table-copy-enabled');
    let selection: TableSelection | null = null;
    let activePointerID: number | null = null;
    let copyHandled = false;

    const copyWithTextareaFallback = (text: string) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none;';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      textarea.remove();
      return copied;
    };

    const clearSelection = () => {
      document.querySelectorAll(`.${SELECTED_CELL_CLASS}`).forEach(cell => cell.classList.remove(SELECTED_CELL_CLASS));
      selection = null;
    };

    const renderSelection = () => {
      document.querySelectorAll(`.${SELECTED_CELL_CLASS}`).forEach(cell => cell.classList.remove(SELECTED_CELL_CLASS));
      if (!selection) return;
      cellRange(selection).forEach(cell => cell.classList.add(SELECTED_CELL_CLASS));
    };

    const updateFocusFromPoint = (clientX: number, clientY: number) => {
      if (!selection) return;
      const nextCell = getTableCell(document.elementFromPoint(clientX, clientY));
      if (!nextCell || nextCell.closest('table') !== selection.table || nextCell === selection.focus) return;
      selection.focus = nextCell;
      renderSelection();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const cell = getTableCell(event.target);
      if (!cell) {
        clearSelection();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      clearSelection();
      selection = { table: cell.closest('table') as HTMLTableElement, anchor: cell, focus: cell };
      activePointerID = event.pointerId;
      cell.setPointerCapture?.(event.pointerId);
      renderSelection();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!selection || activePointerID !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      updateFocusFromPoint(event.clientX, event.clientY);
    };

    const finishPointerSelection = (event: PointerEvent) => {
      if (activePointerID !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      const captureTarget = event.target;
      if (captureTarget instanceof Element && captureTarget.hasPointerCapture?.(event.pointerId)) captureTarget.releasePointerCapture(event.pointerId);
      activePointerID = null;
    };

    const onCopy = (event: ClipboardEvent) => {
      if (!selection || isEditableElement(document.activeElement)) return;
      const text = selectionText(selection);
      if (!text || !event.clipboardData) return;

      event.clipboardData.setData('text/plain', text);
      event.preventDefault();
      copyHandled = true;
      showToast('复制成功', 'success');
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selection) clearSelection();

      if (
        event.key.toLowerCase() !== 'c' ||
        (!event.ctrlKey && !event.metaKey) ||
        event.altKey ||
        !selection ||
        isEditableElement(document.activeElement)
      ) return;

      const text = selectionText(selection);
      if (!text) return;

      // Force a copy event while the keyboard user gesture is still active.
      // Unlike a native text selection, the rectangular cell selection has no
      // browser selection range and may otherwise be skipped by Ctrl/Cmd+C.
      event.preventDefault();
      copyHandled = false;
      document.execCommand('copy');
      if (copyHandled) return;

      // Keep the fallback synchronous. Async clipboard writes can race when
      // users copy several table selections in quick succession.
      if (copyWithTextareaFallback(text)) showToast('复制成功', 'success');
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerup', finishPointerSelection, true);
    document.addEventListener('pointercancel', finishPointerSelection, true);
    document.addEventListener('copy', onCopy, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.classList.remove('marketing-table-copy-enabled');
      clearSelection();
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointermove', onPointerMove, true);
      document.removeEventListener('pointerup', finishPointerSelection, true);
      document.removeEventListener('pointercancel', finishPointerSelection, true);
      document.removeEventListener('copy', onCopy, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [enabled, showToast]);

  return null;
}
